<template>
  <div class="chart-top-block">
    <trade-top-heading-block
      auto-height
      class="chart-top-block__info flx flx--center pl-10 pl-30--m pr-10 pr-0--m"
    >
      <trade-chart-pair-selector
        :disable-market-list="disableMarketList"
        :is-demo="isDemo"
        @toggle-markets-list="$emit('toggle-markets-list')"
      />
      <div
        class="chart-top-block__chart-mobile-toggler"
        @click="$emit('input', !value)"
      >
        <img
          v-if="value"
          :src="require('@/assets/img/trade/unactive-chart-icon.svg')"
          alt=""
          loading="lazy"
        >
        <img
          v-if="!value"
          :src="require('@/assets/img/trade/active-chart-icon.svg')"
          alt=""
          loading="lazy"
        >
      </div>
    </trade-top-heading-block>

    <trade-top-heading-block
      class="
        chart-top-block__items
        pl-12
        pr-20--m
      "
    >
      <div class="chart-top-block__current-data">
        <div :class="changeClass" class="chart-top-block__change">
          <span class="txt--10px txt--color-03">
            {{ $t('trade.marketList.change') }}
          </span>
          <span class="txt--12px txt--weight-500">
            {{ change + '%' }}
          </span>
        </div>
        <div class="chart-top-block__price">
          <span class="txt--10px txt--color-03">
            {{ $t('trade.marketList.lastPrice') }} ({{
              getCurrentPair.moneyTicker
            }})
          </span>
          <span class="txt--12px txt--color-04 txt--weight-500">
            {{ $filters.formatNumber(price) }}
          </span>
        </div>
        <div class="chart-top-block__volume-crypto">
          <span class="txt--10px txt--color-03">
            {{ $t('trade.marketList.volume') }} ({{ amountCurrency }})
          </span>
          <span class="txt--12px txt--color-04 txt--weight-500">
            {{ $filters.shortNumber(volumeCrypto) }}
          </span>
        </div>
        <div class="chart-top-block__volume-fiat">
          <span class="txt--10px txt--color-03">
            {{ $t('trade.marketList.volume') }} ({{ totalCurrency }})
          </span>
          <span class="txt--12px txt--color-04 txt--weight-500">
            {{ $filters.shortNumber(volumeFiat) }}
          </span>
        </div>
      </div>
    </trade-top-heading-block>
  </div>
</template>

<script>
import throttle from 'lodash/throttle'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'TradeChartTopBlock',
  components: {
    TradeTopHeadingBlock: () =>
      import('@/components/trade/TradeTopHeadingBlock.vue'),
    TradeChartPairSelector: () =>
      import('@/components/trade-chart/TradeChartPairSelector.vue'),
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    disableMarketList: {
      type: Boolean,
      default: false,
    },
    isDemo: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      interval: null,
    }
  },
  computed: {
    ...mapGetters({
      amountCurrency: 'pairs/amountCurrency',
      totalCurrency: 'pairs/totalCurrency',
      getCurrentPair: 'pairs/getCurrentPair',
      stakingGroups: 'stakingPool/getStakingGroups',
      currentPair: 'pairs/getCurrentPairName',
    }),
    tickers () {
      return this.currentPair.split('_')
    },
    change () {
      return this.getCurrentPair.change
    },
    stockLabel () {
      return this.getCurrentPair.stockLabel
    },
    price () {
      return this.getCurrentPair.price // return this.getCurrentPair.market.price || '';
    },
    changeClass () {
      return parseFloat(this.change) < 0
        ? 'txt--red'
        : parseFloat(this.change) > 0
          ? 'txt--green'
          : ''
    },
    volumeCrypto () {
      return this.getCurrentPair.volume // .market.volume || ''
    },
    volumeFiat () {
      return this.getCurrentPair.volumeInMoney // .market.volumeInMoney || ''
    },
    marketLink () {
      const ticker = (this.stockLabel || '').replace(/\s/g, '-').toLowerCase()

      return `https://coinmarketcap.com/currencies/${ticker}`
    },
    stakingRoute () {
      // const tickers = this.currentPair.split('_')
      const stakingItem = this.stakingGroups.find((item) => {
        return item.ticker === this.currentPair.split('_')[0]
      })

      if (!stakingItem) return

      if (stakingItem.planCount === 1) {
        // directly to plan details
        return {
          path: this.localePath('/staking-pool/plan/' + stakingItem.redirectId),
          params: { id: stakingItem.redirectId },
        }
      } else {
        // link to items list
        return {
          path: this.localePath('/staking-pool/item/' + stakingItem.ticker),
          params: { ticker: stakingItem.ticker },
        }
      }
    },
  },
  async mounted () {
    if (process.client) {
      await this.getStakingGroup()

      this.interval = setInterval(() => this.updateStakingPool(), 1000 * 30)
    }
  },
  beforeDestroy () {
    clearInterval(this.interval)
  },
  methods: {
    ...mapActions('stakingPool', [
      'getStakingGroup',
      'updateStakingGroup',
      'getStakingPlans',
      'getStakingPlanTickers',
    ]),

    updateStakingPool: throttle(async function () {
      await this.updateStakingGroup()
    }, 1000 * 30),
  },
}
</script>

<style lang="scss" scoped>
.chart-top-block {
  display: flex;
  flex-direction: column;

  @include bp(m) {
    flex-direction: row;
    align-items: center;
  }

  &__items {
    display: flex;
    align-items: center;
    width: 100%;

    @include bp(m) {
      justify-content: flex-end;
    }
  }

  &__current-data {
    display: grid;
    align-items: center;
    height: toRem(37px);
    column-gap: toRem(10px);
    grid-template-areas: 'change price crypto fiat';
    width: 100%;

    @include bp(s) {
      column-gap: toRem(30px);
      width: auto;
    }

    @include bp(m) {
      height: auto;
    }
  }

  &__info {
    display: flex;
    width: 100%;

    @include bp(m) {
      width: auto;
    }
  }

  &__pair {
    grid-area: pair;
    flex: 2;
    text-align: center;

    @include bp(m) {
      flex: unset;
    }
  }

  &__staking-mobile,
  &__market {
    grid-area: market;
    text-align: center;

    @include bp(m) {
      min-width: auto;
    }
  }

  &__staking-mobile {
    min-width: toRem(38px);
  }

  &__market {
    min-width: toRem(37px);
    display: flex;
    justify-content: center;
  }

  &__market-link {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__change {
    grid-area: change;
    display: flex;
    align-items: flex-end;
    flex-direction: column;

    @include bp(0, m) {
      align-items: flex-start;
    }
  }

  &__price {
    grid-area: price;
    display: flex;
    align-items: flex-end;
    flex-direction: column;

    @include bp(0, m) {
      align-items: flex-start;
    }
  }

  &__volume-crypto {
    grid-area: crypto;
    display: flex;
    align-items: flex-end;
    flex-direction: column;

    @include bp(0, m) {
      align-items: flex-start;
    }
  }

  &__volume-fiat {
    grid-area: fiat;
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    flex: 1;

    @include bp(m) {
      flex: auto;
    }

    @include bp(0, m) {
      align-items: flex-start;
    }
  }

  &__chart-mobile-toggler {
    grid-area: toggler;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: toRem(38px);
    height: toRem(37px);

    @include bp(m) {
      display: none;
    }
  }

  &__staking-desktop {
    display: none;

    @include bp(m) {
      display: block;
      margin-left: auto;
    }

    @include bp(l) {
      display: none;
    }

    @include bp(xl) {
      display: block;
    }
  }

  &__staking-mobile,  // 0px-s,xl+
    &__staking-mobile-link // 0px+
 {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__staking-mobile {
    @include bp(m) {
      display: none;
    }

    @include bp(l) {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    @include bp(xl) {
      display: none;
    }
  }
}
</style>
